import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/crm/RichTextEditor";
import { DomainSender } from "@/components/crm/DomainSender";
import { CheckCircle, ShieldCheck, Plugs, LinkSimpleBreak, EnvelopeSimple, PaperPlaneTilt, ArrowClockwise, FileText, Signature, Tray, WarningCircle, PencilSimple, ArrowSquareOut } from "@phosphor-icons/react";

// Fournisseurs proposés dans le sélecteur (config SMTP pré-remplie).
const PROVIDERS = [
  { key: "auto", label: "Détecter automatiquement d'après mon adresse" },
  { key: "spacemail", label: "Spacemail", host: "mail.spacemail.com", port: 465, security: "ssl" },
  { key: "gmail", label: "Gmail / Google Workspace", host: "smtp.gmail.com", port: 587, security: "starttls" },
  { key: "microsoft", label: "Outlook / Microsoft 365", host: "smtp.office365.com", port: 587, security: "starttls" },
  { key: "ovh", label: "OVH", host: "ssl0.ovh.net", port: 465, security: "ssl" },
  { key: "orange", label: "Orange / Wanadoo", host: "smtp.orange.fr", port: 465, security: "ssl" },
  { key: "sfr", label: "SFR", host: "smtp.sfr.fr", port: 465, security: "ssl" },
  { key: "free", label: "Free", host: "smtp.free.fr", port: 465, security: "ssl" },
  { key: "yahoo", label: "Yahoo", host: "smtp.mail.yahoo.com", port: 465, security: "ssl" },
  { key: "icloud", label: "iCloud", host: "smtp.mail.me.com", port: 587, security: "starttls" },
  { key: "other", label: "Autre serveur (personnalisé)", host: "", port: 465, security: "ssl" },
];

// Détection automatique par domaine (fournisseurs grand public).
const DOMAIN_MAP = {
  "gmail.com": "gmail", "googlemail.com": "gmail",
  "outlook.com": "microsoft", "outlook.fr": "microsoft", "hotmail.com": "microsoft",
  "hotmail.fr": "microsoft", "live.com": "microsoft", "live.fr": "microsoft", "office365.com": "microsoft",
  "yahoo.com": "yahoo", "yahoo.fr": "yahoo",
  "orange.fr": "orange", "wanadoo.fr": "orange",
  "free.fr": "free", "sfr.fr": "sfr", "spacemail.com": "spacemail",
  "icloud.com": "icloud", "me.com": "icloud",
};

const inputCls = "bg-surface-2 border-border text-fg";

// Fournisseurs qui EXIGENT un « mot de passe d'application » (le mot de passe habituel est refusé en SMTP).
const APP_PW = {
  gmail: {
    label: "Gmail / Google Workspace",
    url: "https://myaccount.google.com/apppasswords",
    steps: [
      "Activez la validation en 2 étapes sur votre compte Google (obligatoire).",
      "Ouvrez myaccount.google.com/apppasswords et créez un mot de passe d'application (nom : interKO).",
      "Copiez le code de 16 caractères et collez-le ci-dessous (sans les espaces).",
    ],
  },
  microsoft: {
    label: "Outlook / Microsoft 365",
    url: "https://account.microsoft.com/security",
    steps: [
      "Si la double authentification est activée, un mot de passe d'application est requis.",
      "Créez-le dans la sécurité de votre compte Microsoft, puis collez-le ci-dessous.",
    ],
  },
  yahoo: {
    label: "Yahoo",
    url: "https://login.yahoo.com/account/security",
    steps: ["Générez un « mot de passe d'application » dans la sécurité Yahoo et collez-le ci-dessous."],
  },
  icloud: {
    label: "iCloud",
    url: "https://appleid.apple.com",
    steps: ["Créez un « mot de passe pour application » sur appleid.apple.com (rubrique Sécurité) et collez-le ci-dessous."],
  },
};

export default function EmailSettings() {
  const [account, setAccount] = useState(undefined);
  const [provider, setProvider] = useState("auto");
  const [email, setEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [password, setPassword] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState(465);
  const [security, setSecurity] = useState("ssl");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [signature, setSignature] = useState("");
  const [savingSig, setSavingSig] = useState(false);
  const [syncingNow, setSyncingNow] = useState(false);
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState(993);
  const [savingImap, setSavingImap] = useState(false);
  const [imapEdit, setImapEdit] = useState(false);
  const [mode, setMode] = useState("mailbox");

  const load = async () => {
    try {
      const { data } = await api.get("/crm/email-account");
      setAccount(data); setSignature(data?.signature_html || "");
      if (data) { setImapHost(data.imap_host || ""); setImapPort(data.imap_port || 993); }
    } catch { setAccount(null); }
  };
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setSyncingNow(true);
    try {
      const { data } = await api.post("/crm/email-account/sync-now");
      setAccount((a) => a ? { ...a, last_inbox_synced_at: data.last_inbox_synced_at, last_inbox_error: data.last_inbox_error } : a);
      if (data.last_inbox_error) toast.error(`Réception impossible : ${data.last_inbox_error}`);
      else if (data.new_replies > 0) toast.success(`${data.new_replies} nouveau(x) message(s) reçu(s) ✅`);
      else toast.success("Boîte à jour — aucune nouvelle réponse.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSyncingNow(false); }
  };

  const saveImap = async () => {
    if (!imapHost.trim()) { toast.error("Indiquez le serveur de réception (IMAP)."); return; }
    setSavingImap(true);
    try {
      const { data } = await api.patch("/crm/email-account/imap", { imap_host: imapHost.trim(), imap_port: Number(imapPort) || 993 });
      setAccount(data); setImapEdit(false);
      toast.success("Serveur de réception mis à jour. Lancez une synchronisation pour vérifier.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingImap(false); }
  };

  const saveSignature = async () => {
    setSavingSig(true);
    try {
      await api.patch("/crm/email-account/signature", { signature_html: signature });
      setAccount((a) => a ? { ...a, signature_html: signature } : a);
      toast.success("Signature enregistrée ✅ — elle sera ajoutée à vos emails.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingSig(false); }
  };

  const domain = email.includes("@") ? email.split("@")[1].toLowerCase().trim() : "";
  const autoKey = DOMAIN_MAP[domain];
  const autoProvider = PROVIDERS.find((p) => p.key === autoKey);

  const pickProvider = (k) => {
    setProvider(k);
    const p = PROVIDERS.find((x) => x.key === k);
    if (p && p.host) { setHost(p.host); setPort(p.port); setSecurity(p.security); }
  };

  const connect = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Saisissez une adresse email valide."); return; }
    if (!password) { toast.error("Saisissez le mot de passe de votre boîte mail."); return; }
    const payload = { email: email.trim(), from_name: fromName.trim(), password };

    if (provider === "other") {
      if (!host.trim()) { toast.error("Indiquez le serveur d'envoi (SMTP) de votre hébergeur."); return; }
      payload.host = host.trim(); payload.port = Number(port); payload.security = security;
    } else if (provider === "auto") {
      if (autoProvider) { payload.host = autoProvider.host; payload.port = autoProvider.port; payload.security = autoProvider.security; }
      // sinon : le backend renverra un message invitant à choisir le fournisseur
    } else {
      const p = PROVIDERS.find((x) => x.key === provider);
      if (p && p.host) { payload.host = p.host; payload.port = p.port; payload.security = p.security; }
    }

    setSaving(true);
    try {
      const { data } = await api.post("/crm/email-account/connect", payload);
      setAccount(data);
      toast.success("Email connecté ✅ — vos messages partiront de cette adresse.");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      toast.error(msg);
      // Si le fournisseur n'a pas pu être reconnu automatiquement, on invite à le choisir.
      if (/reconnu|fournisseur/i.test(msg || "") && provider === "auto") setProvider("other");
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    try { await api.delete("/crm/email-account"); setAccount(null); setPassword(""); toast.success("Email déconnecté."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/crm/email-account/test");
      toast.success(`Email de test envoyé à ${data.to} ✅ — vérifiez votre boîte de réception.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setTesting(false); }
  };

  const toggleSync = async (enabled) => {
    setSyncing(true);
    try {
      await api.patch("/crm/email-account/inbox-sync", { enabled });
      setAccount((a) => ({ ...a, inbox_sync_enabled: enabled }));
      toast.success(enabled ? "Récupération des réponses activée." : "Récupération des réponses désactivée.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSyncing(false); }
  };

  const showCustom = provider === "other";
  const effProvider = provider === "auto" ? autoKey : provider;
  const appPw = APP_PW[effProvider];

  return (
    <div className="max-w-lg" data-testid="email-settings-page">
      <div className="overline mb-2">Réglages</div>
      <h1 className="font-display text-3xl font-bold">Connecter mon email</h1>
      <p className="text-muted text-sm mt-1 mb-8">Connectez votre email professionnel pour envoyer vos messages <b>depuis votre propre adresse</b>. C'est aussi simple que d'ajouter un compte sur votre téléphone.</p>

      {account === undefined ? (
        <div className="text-muted text-sm animate-pulse">Chargement…</div>
      ) : account ? (
        <div className="surface rounded-lg p-6" data-testid="email-connected">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-accent/12 border border-accent/30 flex items-center justify-center">
              <CheckCircle size={22} weight="fill" className="text-accent" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-fg flex items-center gap-2">{account.email} <ShieldCheck size={15} className="text-accent" weight="fill" /></div>
              <div className="text-xs text-muted">{account.provider === "resend" ? `Envoi via votre domaine ${account.sender_domain} (vérifié)` : "Email connecté · vos messages partent de cette adresse"}</div>
            </div>
          </div>

          {/* Identité d'expéditeur */}
          <div className="mt-4 rounded-md bg-surface-2/50 border border-border p-3" data-testid="email-sender-identity">
            <div className="overline mb-2">Identité d'expéditeur</div>
            <div className="text-sm text-fg">
              <span className="font-medium">{account.from_name || "—"}</span>
              <span className="text-muted"> &lt;{account.email}&gt;</span>
            </div>
            <p className="text-[11px] text-muted mt-1">C'est ce que voient vos destinataires. Le nom affiché provient de votre profil ; pour le changer, reconnectez votre boîte avec un autre nom.</p>
          </div>

          {/* Test + synchro des réponses */}
          <div className="mt-5 pt-4 border-t border-border space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={sendTest} disabled={testing} data-testid="email-test-btn"
                className="flex items-center gap-2 text-sm bg-accent text-black font-semibold rounded-md px-4 py-2 hover-lift disabled:opacity-60">
                <PaperPlaneTilt size={15} weight="fill" /> {testing ? "Envoi du test…" : "Envoyer un email de test à moi-même"}
              </button>
              <span className="text-xs text-muted">Confirmez d'un coup d'œil que votre email fonctionne.</span>
            </div>

            {account.provider === "resend" ? (
              <p className="text-xs text-muted" data-testid="email-domain-replies-note">Les réponses des décideurs arrivent directement dans votre boîte habituelle (<b>{account.email}</b>). Elles ne sont pas relevées automatiquement dans interKO avec ce mode d'envoi.</p>
            ) : (
            <label className="flex items-start gap-3 cursor-pointer" data-testid="email-inbox-sync-row">
              <input type="checkbox" checked={!!account.inbox_sync_enabled} disabled={syncing}
                onChange={(e) => toggleSync(e.target.checked)} className="accent-[#00E5FF] mt-0.5" data-testid="email-inbox-sync-toggle" />
              <span>
                <span className="text-sm text-fg flex items-center gap-1.5"><ArrowClockwise size={14} className="text-accent" /> Récupérer les réponses des décideurs dans interKO</span>
                <span className="block text-xs text-muted mt-0.5">Les réponses à vos emails apparaissent dans le fil de discussion et stoppent automatiquement les relances programmées.</span>
              </span>
            </label>
            )}
          </div>

          {/* Réception des réponses (IMAP) */}
          {account.provider !== "resend" && (
          <div className="mt-5 pt-4 border-t border-border" data-testid="email-imap-block">
            <div className="flex items-center gap-2 mb-2">
              <Tray size={15} className="text-accent" />
              <span className="text-sm font-display font-semibold text-fg">Réception des messages (IMAP)</span>
            </div>
            <p className="text-xs text-muted mb-3">
              La réception est <b>configurée automatiquement</b> d'après votre fournisseur — pas de manipulation nécessaire. interKO relève votre boîte toutes les 15 minutes. Seules les <b>réponses à vos emails</b> apparaissent dans « Réception » ; les autres emails vont dans « Non rattachés », sans notification.
            </p>

            <div className="rounded-md bg-surface-2/50 border border-border p-3 space-y-2">
              {!imapEdit ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="overline mb-0.5">Serveur de réception</div>
                    <div className="text-sm text-fg font-mono truncate" data-testid="email-imap-server">
                      {account.imap_host || "—"}{account.imap_port ? `:${account.imap_port}` : ""}
                    </div>
                  </div>
                  <button onClick={() => setImapEdit(true)} data-testid="email-imap-edit"
                    className="shrink-0 flex items-center gap-1.5 text-xs text-muted hover:text-fg border border-border rounded-md px-2.5 py-1.5 hover:bg-surface-2">
                    <PencilSimple size={13} /> Modifier
                  </button>
                </div>
              ) : (
                <div className="space-y-2" data-testid="email-imap-edit-form">
                  <div className="grid grid-cols-[1fr_88px] gap-2">
                    <div>
                      <label className="overline block mb-1">Serveur IMAP</label>
                      <Input className={inputCls} value={imapHost} onChange={(e) => setImapHost(e.target.value)} placeholder="imap.mon-entreprise.fr" data-testid="email-imap-host" />
                    </div>
                    <div>
                      <label className="overline block mb-1">Port</label>
                      <Input className={inputCls} value={imapPort} onChange={(e) => setImapPort(e.target.value)} data-testid="email-imap-port" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={saveImap} disabled={savingImap} data-testid="email-imap-save"
                      className="text-xs bg-accent text-black font-semibold px-3 py-1.5 rounded-md hover-lift disabled:opacity-60">
                      {savingImap ? "Enregistrement…" : "Enregistrer"}
                    </button>
                    <button onClick={() => { setImapEdit(false); setImapHost(account.imap_host || ""); setImapPort(account.imap_port || 993); }}
                      className="text-xs text-muted hover:text-fg px-2 py-1.5">Annuler</button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-[11px] text-muted" data-testid="email-imap-last-sync">
                  {account.last_inbox_synced_at
                    ? `Dernière relève : ${new Date(account.last_inbox_synced_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
                    : "Jamais synchronisé"}
                </span>
                <button onClick={syncNow} disabled={syncingNow} data-testid="email-sync-now-btn"
                  className="shrink-0 flex items-center gap-1.5 text-xs font-semibold border border-accent/40 text-accent rounded-md px-3 py-1.5 hover:bg-accent/10 disabled:opacity-60">
                  <ArrowClockwise size={13} className={syncingNow ? "animate-spin" : ""} /> {syncingNow ? "Relève en cours…" : "Synchroniser maintenant"}
                </button>
              </div>

              {account.last_inbox_error && (
                <div className="flex items-start gap-1.5 text-[11px] text-destructive bg-destructive/10 border border-destructive/30 rounded px-2 py-1.5" data-testid="email-imap-error">
                  <WarningCircle size={13} className="mt-0.5 shrink-0" />
                  <span>Dernière erreur de réception : {account.last_inbox_error}. Vérifiez le serveur IMAP ci-dessus (et que l'IMAP est activé chez votre fournisseur).</span>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Signature */}
          <div className="mt-5 pt-4 border-t border-border" data-testid="email-signature-block">
            <div className="flex items-center gap-2 mb-2">
              <Signature size={15} className="text-accent" />
              <span className="text-sm font-display font-semibold text-fg">Votre signature</span>
            </div>
            <p className="text-xs text-muted mb-2">Ajoutée automatiquement au bas de chaque email envoyé (nom, poste, société, téléphone…).</p>
            <RichTextEditor value={signature} onChange={setSignature} testid="email-signature-editor" minHeight={120} />
            {signature && signature.replace(/<[^>]*>/g, "").trim() && (
              <div className="mt-2" data-testid="email-signature-preview">
                <div className="overline mb-1">Aperçu</div>
                <div className="rounded-md border border-border bg-white text-black p-3 text-sm" dangerouslySetInnerHTML={{ __html: signature }} />
              </div>
            )}
            <button onClick={saveSignature} disabled={savingSig} data-testid="email-signature-save"
              className="mt-2 text-sm bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-60">
              {savingSig ? "Enregistrement…" : "Enregistrer la signature"}
            </button>
          </div>

          <div className="mt-5 pt-4 border-t border-border flex items-center gap-3 flex-wrap">
            <button onClick={disconnect} data-testid="email-disconnect-btn"
              className="flex items-center gap-2 text-sm border border-border rounded-md px-4 py-2 text-muted hover:text-destructive hover:border-destructive/40">
              <LinkSimpleBreak size={15} /> Déconnecter
            </button>
            <Link to="/app/settings/templates" data-testid="email-templates-link"
              className="flex items-center gap-2 text-sm text-accent hover:underline">
              <FileText size={15} /> Gérer mes modèles d'emails
            </Link>
          </div>
        </div>
      ) : (
        <>
        <div className="flex items-center gap-1 bg-surface border border-border rounded-md p-1 mb-4 w-fit" data-testid="email-mode-toggle">
          {[["mailbox", "Ma boîte mail (mot de passe)"], ["domain", "Mon nom de domaine (sans mot de passe)"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`email-mode-${k}`}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${mode === k ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"}`}>{l}</button>
          ))}
        </div>
        {mode === "domain" ? (
          <div className="surface rounded-lg p-6" data-testid="email-domain-form"><DomainSender onConnected={setAccount} /></div>
        ) : (
        <div className="surface rounded-lg p-6 space-y-4" data-testid="email-connect-form">
          {/* CHOISIR VOTRE FOURNISSEUR — bien visible */}
          <div className="rounded-lg border border-accent/30 bg-accent/[0.06] p-4">
            <label className="block text-sm font-display font-bold tracking-wide text-accent mb-2">CHOISIR VOTRE FOURNISSEUR</label>
            <Select value={provider} onValueChange={pickProvider}>
              <SelectTrigger className="bg-surface-2 border-accent/40 text-fg h-11" data-testid="email-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted mt-2">Sélectionnez le service qui héberge votre boîte mail. Si vous ne le connaissez pas, laissez « Détecter automatiquement » ou demandez-le à votre support informatique.</p>
          </div>

          <div>
            <label className="overline block mb-1.5">Adresse email professionnelle</label>
            <div className="relative">
              <EnvelopeSimple size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <Input className={`${inputCls} pl-9`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nom@mon-entreprise.fr" data-testid="email-address" />
            </div>
            {provider === "auto" && autoProvider && (
              <p className="text-xs text-accent mt-1.5 flex items-center gap-1.5" data-testid="email-detected">
                <CheckCircle size={13} weight="fill" /> Fournisseur reconnu : {autoProvider.label} — rien d'autre à configurer.
              </p>
            )}
          </div>

          <div>
            <label className="overline block mb-1.5">Nom affiché (optionnel)</label>
            <Input className={inputCls} value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Paul Martin" data-testid="email-from-name" />
          </div>

          <div>
            <label className="overline block mb-1.5">
              {appPw ? "Mot de passe d'application (pas votre mot de passe habituel)" : "Mot de passe de connexion à votre boîte mail"}
            </label>
            {appPw && (
              <div className="mb-2 rounded-md border border-amber-400/40 bg-amber-400/[0.08] p-3" data-testid="email-app-password-help">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-500">
                  <WarningCircle size={14} weight="fill" /> {appPw.label} exige un « mot de passe d'application »
                </div>
                <p className="text-[11px] text-muted mt-1">
                  Pour votre sécurité, {appPw.label} refuse votre mot de passe habituel dans les applications tierces. Créez un mot de passe dédié à interKO :
                </p>
                <ol className="text-[11px] text-muted mt-1.5 space-y-0.5 list-decimal list-inside">
                  {appPw.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
                <a href={appPw.url} target="_blank" rel="noreferrer" data-testid="email-app-password-link"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-accent hover:underline mt-2">
                  <ArrowSquareOut size={13} /> Créer mon mot de passe d'application
                </a>
                {effProvider === "gmail" && (
                  <button onClick={() => setMode("domain")} data-testid="email-use-domain-btn"
                    className="block text-[11px] font-semibold text-accent hover:underline mt-1.5">
                    Mots de passe d'application indisponibles (Google Workspace) ? Envoyer via mon nom de domaine →
                  </button>
                )}
              </div>
            )}
            <Input type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={appPw ? "Code à 16 caractères (ex. abcd efgh ijkl mnop)" : "••••••••••"} data-testid="email-password" />
            <p className="text-xs text-muted mt-1.5">
              {appPw
                ? "Collez le mot de passe d'application généré ci-dessus. 🔒 Il est chiffré et n'est jamais réaffiché."
                : "Le mot de passe que vous utilisez pour vous connecter à votre webmail. 🔒 Il est chiffré et n'est jamais réaffiché."}
            </p>
          </div>

          {/* Serveur personnalisé (uniquement si « Autre serveur ») */}
          {showCustom && (
            <div className="space-y-3 border-t border-border pt-4" data-testid="email-custom">
              <p className="text-xs text-muted">Réglages fournis par votre hébergeur email.</p>
              <div>
                <label className="overline block mb-1.5">Serveur d'envoi (SMTP)</label>
                <Input className={inputCls} value={host} onChange={(e) => setHost(e.target.value)} placeholder="mail.mon-entreprise.fr" data-testid="email-host" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-1.5">Port</label>
                  <Input className={inputCls} value={port} onChange={(e) => setPort(e.target.value)} data-testid="email-port" />
                </div>
                <div>
                  <label className="overline block mb-1.5">Chiffrement</label>
                  <Select value={security} onValueChange={setSecurity}>
                    <SelectTrigger className={inputCls} data-testid="email-security"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ssl">SSL/TLS (465)</SelectItem>
                      <SelectItem value="starttls">STARTTLS (587)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <button onClick={connect} disabled={saving} data-testid="email-connect-btn"
            className="flex items-center gap-2 bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift disabled:opacity-60 w-full justify-center">
            <Plugs size={16} weight="bold" /> {saving ? "Connexion en cours…" : "Connecter mon email"}
          </button>
        </div>
        )}
        </>
      )}
    </div>
  );
}
